import EmailStatisticsEmailFilter from 'modules/mailing/email-statistics/components/email-statistics-email-filter'
import { EmailStatisticsStatusFilter } from 'modules/mailing/email-statistics/components/email-statistics-status-filter'
import { emailClickLinkStatisticsTableColumns } from 'modules/mailing/email-statistics/configs/email-click-link-statistics-table-columns'
import { emailStatisticsTableColumns } from 'modules/mailing/email-statistics/configs/email-statistics-table-columns'
import { useEmailClickLinkStatistics } from 'modules/mailing/email-statistics/hooks/use-email-click-link-statistics'
import { useEmailStatistics } from 'modules/mailing/email-statistics/hooks/use-email-statistics'
import {
  StatisticsFilterApiInterface,
  StatisticsFilterInterface,
} from 'modules/mailing/email-statistics/types/email-statistics-filter-interface'
import { MailingId } from 'modules/mailing/types/mailing-interface'
import React, { useCallback, useEffect, useState } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { useFilterWithAbort } from 'shared/hooks/use-filter-with-abort'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { deepCompare } from 'shared/utils/deepCompare'

interface MailingStatisticProps {
  mailingId: MailingId
  isAdminPage?: boolean
}

const defaultFilters: StatisticsFilterInterface = {
  contacts: [],
  opened: undefined,
  clicked: undefined,
  spam: undefined,
  bounced: undefined,
}

export function MailingStatistic({ mailingId, isAdminPage }: MailingStatisticProps) {
  const { t } = useLocoTranslation()

  const { user } = useUser()

  const { setPagination, pagination, setLimit, limit, filter, setFilter } =
    useFilterPagination(defaultFilters)

  const [responsiveFilter, setResponsiveFilter] =
    useState<StatisticsFilterInterface>(defaultFilters)

  useEffect(() => {
    setResponsiveFilter(filter)
  }, [filter])

  const apiFilter: StatisticsFilterApiInterface = {
    bounced: filter.bounced,
    clicked: filter.clicked,
    opened: filter.opened,
    spam: filter.spam,
    contactIds: filter.contacts.map(el => el.id),
  }

  const { statistics, counter, isStatisticsValidating, abortStatistics } = useEmailStatistics({
    emailId: mailingId,
    limit,
    pagination,
    filter: apiFilter,
  })

  const handleFilter = useCallback(() => {
    setFilter(responsiveFilter)
    setPagination({ order: OrderEnum.next })
    setLimit(10)
  }, [responsiveFilter, setFilter, setLimit, setPagination])

  useFilterWithAbort({
    handleFilter,
    onAbort: abortStatistics,
    shouldUpdate: !deepCompare(filter, responsiveFilter),
  })

  const { data: emailClickLinkStatistics } = useEmailClickLinkStatistics({ mailingId })

  const totalClicksForTheEmail =
    emailClickLinkStatistics?.items.reduce((acc, item) => {
      return acc + item.totalClickAmount
    }, 0) ?? 0

  return (
    <div className={'flex flex-col gap-5'}>
      <ListingLayout
        counter={counter}
        data={isStatisticsValidating ? undefined : statistics}
        title={t('dashboard.email_statistics.table.title')}
        type={'default'}
        headerClassName={'flex-wrap'}
        customHeaderElement={
          <div className={'flex flex-wrap-reverse items-center gap-3'}>
            <EmailStatisticsEmailFilter
              contacts={responsiveFilter.contacts}
              onChangeContacts={contacts =>
                setResponsiveFilter(prev => ({
                  ...prev,
                  contacts,
                }))
              }
            />
            <EmailStatisticsStatusFilter
              setFilter={setResponsiveFilter}
              filter={responsiveFilter}
            />
          </div>
        }
      >
        <Table
          data={isStatisticsValidating ? undefined : statistics}
          columns={emailStatisticsTableColumns({ disableEmailLink: isAdminPage, user, t })}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
        />
      </ListingLayout>

      <ListingLayout
        counter={false}
        data={emailClickLinkStatistics}
        title={t('dashboard.email_statistics.table_links.title')}
        type={'default'}
      >
        <Table
          data={emailClickLinkStatistics}
          columns={emailClickLinkStatisticsTableColumns({ t, totalClicksForTheEmail, mailingId })}
          paginationIdField="link"
        />
      </ListingLayout>
    </div>
  )
}
